import React from "react";
import {
  CARD_SIZES_1UP,
  CARD_SIZES_2UP_40,
  CARD_SIZES_2UP_60,
  CARD_SIZES_2UP_GRID,
  CARD_SIZES_3UP,
  CARD_SIZES_4UP,
} from "@/lib/imageSizes";
import CardContentItem from "@/components/cards/cardContent/cardContent";
import { CardSection as CardSectionType, ContentCard } from "@/types/cms";
import { cn } from "@/lib/utils";
import styles from "./cardsGrid.module.css";

type CardSectionTypeEnum = CardSectionType['type'];

interface CardsGridProps {
  type?: CardSectionTypeEnum;
  cards: ContentCard[];
  shadow?: boolean;
  variant?: "default" | "testimonial" | "price";
  getCardVariant?: (card: ContentCard) => "default" | "testimonial" | "price";
}

/**
 * `sizes` per grid density, so thumbnails request only what their slot needs.
 * Mirrors the column counts in cardsGrid.module.css. Cards rendered outside
 * this grid (hero, 1-up, 2-up slots) keep CardContentItem's larger default.
 */
const SIZES_BY_TYPE: Record<string, string> = {
  type_4up: CARD_SIZES_4UP,
  "type_2up50/50": CARD_SIZES_2UP_GRID,
  type_1up: CARD_SIZES_1UP,
  type_1up100vh: CARD_SIZES_1UP,
  "type_2up60/40": CARD_SIZES_2UP_60,
  "type_2up40/60": CARD_SIZES_2UP_40,
};
const SIZES_DEFAULT = CARD_SIZES_3UP;

const CardsGrid = ({
  type = "type_3up",
  cards,
  shadow = false,
  variant,
  getCardVariant,
}: CardsGridProps) => {
  let layoutClass: string;

  switch (type) {
    case 'type_4up':
      layoutClass = styles.type4up;
      break;
    case 'type_2up50/50':
      layoutClass = styles.type2up;
      break;
    case 'type_1up':
      layoutClass = styles.type1up;
      break;
    case 'type_1up100vh':
      layoutClass = styles.type1up100vh;
      break;
    case 'type_2up60/40':
      layoutClass = styles.type2up60_40;
      break;
    case 'type_2up40/60':
      layoutClass = styles.type2up40_60;
      break;
    default:
      layoutClass = styles.typeDefault;
      break;
  }

  return (
    <div className={cn(styles.grid, layoutClass)}>
      {cards.map((card, idx) => (
        <CardContentItem
          key={card.id ?? `${idx}-${card.header || 'card'}`}
          card={card}
          shadow={shadow}
          variant={getCardVariant ? getCardVariant(card) : variant}
          imageSizes={SIZES_BY_TYPE[type as string] ?? SIZES_DEFAULT}
        />
      ))}
    </div>
  );
};

export default CardsGrid;


